// Data Service for managing song categories and songs
class DataService {
  constructor() {
    this.cache = new Map();
    this.config = null;
    this.categories = null;
  }

  // Load configuration
  async loadConfig() {
    // Temporarily disable caching to ensure fresh data
    // if (this.config) return this.config;
    
    try {
      const response = await fetch(`/data/config.json?t=${Date.now()}`);
      this.config = await response.json();
      return this.config;
    } catch (error) {
      console.error('Error loading config:', error);
      return this.getDefaultConfig();
    }
  }

  // Load categories
  async loadCategories() {
    // Temporarily disable caching to ensure fresh data
    // if (this.categories) return this.categories;
    
    console.log('Loading fresh categories');
    
    try {
      const response = await fetch(`/data/categories.json?t=${Date.now()}`);
      const data = await response.json();
      this.categories = data.categories.filter(cat => cat.enabled);
      console.log(`Loaded ${this.categories.length} categories`);
      return this.categories;
    } catch (error) {
      console.error('Error loading categories:', error);
      return [];
    }
  }

  // Load songs for a specific category
  async loadSongs(categoryId) {
    const cacheKey = `songs_${categoryId}`;
    // Temporarily disable caching to ensure fresh data
    // if (this.cache.has(cacheKey)) {
    //   return this.cache.get(cacheKey);
    // }

    console.log(`Loading fresh songs for category: ${categoryId}`);

    try {
      const categories = await this.loadCategories();
      const category = categories.find(cat => cat.id === categoryId);
      
      if (!category) {
        throw new Error(`Category ${categoryId} not found`);
      }

      const timestamp = Date.now();
      const url = `/data/songs/${category.songFile}?t=${timestamp}`;
      console.log(`Fetching from: ${url}`);

      const response = await fetch(url);
      const data = await response.json();
      
      console.log(`Loaded ${data.songs.length} songs from ${category.songFile}`);
      
      // Process songs and add full YouTube URLs
      const config = await this.loadConfig();
      const processedSongs = data.songs
        .filter(song => song.enabled)
        .map(song => ({
          ...song,
          youtubeUrl: `${config.youtube.baseUrl}${song.youtubeId}`,
          embedUrl: `${config.youtube.embedUrl}${song.youtubeId}`
        }));

      this.cache.set(cacheKey, processedSongs);
      console.log(`Processed ${processedSongs.length} enabled songs`);
      return processedSongs;
    } catch (error) {
      console.error(`Error loading songs for ${categoryId}:`, error);
      return [];
    }
  }

  // Get all songs across all categories
  async getAllSongs() {
    try {
      const categories = await this.loadCategories();
      const allSongs = [];
      
      for (const category of categories) {
        if (category.songFile) {
          const songs = await this.loadSongs(category.id);
          allSongs.push(...songs);
        }
      }
      
      return allSongs;
    } catch (error) {
      console.error('Error loading all songs:', error);
      return [];
    }
  }

  // Search songs by title, artist, or tags
  async searchSongs(query) {
    const allSongs = await this.getAllSongs();
    const searchTerm = query.toLowerCase();

    return allSongs.filter(song => 
      song.title.toLowerCase().includes(searchTerm) ||
      song.artist.toLowerCase().includes(searchTerm) ||
      song.tags.some(tag => tag.toLowerCase().includes(searchTerm))
    );
  }

  // Get songs by tag
  async getSongsByTag(tag) {
    const allSongs = await this.getAllSongs();
    return allSongs.filter(song => 
      song.tags.includes(tag)
    );
  }

  // Get random songs from a category
  async getRandomSongs(categoryId, count = 5) {
    const songs = await this.loadSongs(categoryId);
    const shuffled = [...songs].sort(() => 0.5 - Math.random());
    return shuffled.slice(0, count);
  }

  // Clear cache
  clearCache() {
    this.cache.clear();
    this.config = null;
    this.categories = null;
    console.log('Cache cleared - data will be reloaded fresh');
  }

  // Get statistics
  async getStatistics() {
    const categories = await this.loadCategories();
    const stats = {
      totalCategories: categories.length,
      totalSongs: 0,
      categoryStats: {}
    };

    for (const category of categories) {
      const songs = await this.loadSongs(category.id);
      stats.totalSongs += songs.length;
      stats.categoryStats[category.id] = {
        title: category.title,
        count: songs.length,
        icon: category.icon
      };
    }

    return stats;
  }

  // Default configuration fallback
  getDefaultConfig() {
    return {
      app: {
        name: "இதய ராகம்",
        subtitle: "Idhaya Ragam",
        version: "1.0.0"
      },
      youtube: {
        baseUrl: "https://www.youtube.com/watch?v=",
        embedUrl: "https://www.youtube.com/embed/",
        openInNewTab: true
      },
      ui: {
        songsPerPage: 20,
        enableSearch: true,
        showSongCount: true
      }
    };
  }

  // Export data for backup
  async exportData() {
    const categories = await this.loadCategories();
    const exportData = {
      categories,
      timestamp: new Date().toISOString(),
      version: this.config?.app?.version || '1.0.0'
    };

    for (const category of categories) {
      const songs = await this.loadSongs(category.id);
      exportData[category.id] = songs;
    }

    return exportData;
  }
}

// Create singleton instance
const dataService = new DataService();
export default dataService;
